import {
  Contact,
  LeaseExtended,
  TransactionLinkType,
  TransactionWithoutPostings,
  getContactNameFromObject,
  getTenantsName,
  uniquePush,
} from '@rentguru/commons-utils';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { IntlFormatters } from 'react-intl';

import RouteDestination from 'src/components/Routes/RouteDestination';

export interface TransactionLinkTarget {
  primary: string;
  secondary: string;
}

export interface TransactionLinkLine {
  id: string;
  label: string;
  amount: number;
}

export interface ReconciliationLocationState {
  leaseIds: string[];
  transactionId?: string;
  invoiceId?: string;
  goBackUrl?: string;
}

export interface ReconciliationLocation {
  pathname: RouteDestination;
  state: ReconciliationLocationState;
}

export const getTransactionLinkTarget = (
  transaction: TransactionWithoutPostings,
  getLease: (id: string) => LeaseExtended | null | undefined,
  getContact: (id: string) => Contact | undefined,
  formatMessage: IntlFormatters['formatMessage']
): TransactionLinkTarget | null => {
  const links = transaction.links ?? [];
  if (links.length > 1) {
    return {
      primary: formatMessage({ id: 'transactions.list.numberOfLinks' }, { count: links.length }),
      secondary: '',
    };
  }
  if (isEmpty(links)) {
    return null;
  }
  const { linkType, linkId } = links[0];
  if (linkType === TransactionLinkType.LEASE) {
    const lease = getLease(linkId);
    if (isNil(lease)) {
      return null;
    }
    const unitNames = (lease.units ?? []).map((unitLease) => unitLease?.unit?.name ?? '').join(', ');
    return { primary: `${lease.name} - ${unitNames}`, secondary: getTenantsName(lease.tenants) };
  }
  const contact = getContact(linkId);
  if (isNil(contact)) {
    return null;
  }
  return { primary: getContactNameFromObject(contact), secondary: '' };
};

export const getTransactionLinkLabel = (
  linkType: TransactionLinkType,
  linkId: string,
  getLease: (id: string) => LeaseExtended | null | undefined,
  getContact: (id: string) => Contact | undefined
): string | null => {
  if (linkType === TransactionLinkType.LEASE) {
    const lease = getLease(linkId);
    return isNil(lease) ? null : lease.name;
  }
  const contact = getContact(linkId);
  return isNil(contact) ? null : getContactNameFromObject(contact);
};

export const getTransactionLinkLines = (
  transaction: TransactionWithoutPostings,
  getLease: (id: string) => LeaseExtended | null | undefined,
  getContact: (id: string) => Contact | undefined,
  unknownLabel: string
): TransactionLinkLine[] =>
  (transaction.links ?? []).map((link, index) => ({
    id: `${link.linkType}:${link.linkId}:${index}`,
    label: getTransactionLinkLabel(link.linkType, link.linkId, getLease, getContact) ?? unknownLabel,
    amount: link.amount,
  }));

export const getTransactionLeaseIds = (transaction: Pick<TransactionWithoutPostings, 'links'>): string[] => {
  const leaseIds: string[] = [];
  (transaction.links ?? []).forEach((link) => {
    if (link.linkType === TransactionLinkType.LEASE) {
      uniquePush(leaseIds, link.linkId);
    }
  });
  return leaseIds;
};

export const buildReconciliationLocation = (
  leaseIds: string[],
  transactionId?: string,
  invoiceId?: string | null
): ReconciliationLocation => ({
  pathname: RouteDestination.RECONCILE_TRANSACTION,
  state: {
    leaseIds,
    ...(!isNil(transactionId) && { transactionId }),
    ...(!isNil(invoiceId) && { invoiceId }),
  },
});
